// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: user settings tab for the Outlook calendar — week start, which
// calendars are shown, and calendar-driven automatic presence.

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import KonvoCalendar from '@app/features/konvo_calendar/state/KonvoCalendar';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import {MondayReportPeople} from '@app/features/konvo_reports/components/MondayReportPeople';
import {ColorPickerField} from '@app/features/ui/components/form/ColorPickerField';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {
	BellIcon,
	CalendarCheckIcon,
	ChartBarIcon,
	ClockIcon,
	SunHorizonIcon,
	SunIcon,
} from '@app/features/ui/icons/Icons';
import {Segmented, type SegmentedOption} from '@app/features/ui/konvo_controls/KonvoControls';
import styles from '@app/features/user/components/modals/tabs/KonvoCalendarSettingsTab.module.css';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useMemo} from 'react';

const START_OF_THE_WEEK_DESCRIPTOR = msg({
	message: 'Start of the week',
	comment: 'Accessible name of the week start control in Calendar settings (Monday or Sunday).',
});
const WEEK_START_MONDAY_DESCRIPTOR = msg({
	message: 'Monday',
	comment: 'Week start option in calendar settings.',
});
const WEEK_START_SUNDAY_DESCRIPTOR = msg({
	message: 'Sunday',
	comment: 'Week start option in calendar settings.',
});
const AUTO_PRESENCE_DESCRIPTOR = msg({
	message: 'Set my status to In a meeting automatically',
	comment: 'Calendar settings toggle for calendar-driven presence.',
});
const AUTO_PRESENCE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'While a calendar event is running, your status switches to In a meeting and back afterwards.',
	comment: 'Description under the calendar auto-presence toggle.',
});
const MEETING_REMINDERS_DESCRIPTOR = msg({
	message: 'DM me before meetings',
	comment: 'Calendar settings toggle for meeting reminder DMs.',
});
const MEETING_REMINDERS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Konvo sends you a direct message five minutes before an event starts, with join links.',
	comment: 'Description under the meeting reminders toggle.',
});
const DAILY_AGENDA_DESCRIPTOR = msg({
	message: 'Morning agenda',
	comment: 'Calendar settings toggle for the daily agenda DM.',
});
const DAILY_AGENDA_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Every weekday morning Konvo DMs you a card with your meetings, open ClickUp tasks, and who is on leave.',
	comment: 'Description under the daily agenda toggle.',
});
const MONDAY_REPORT_DESCRIPTOR = msg({
	message: 'Monday Madness report',
	comment: 'Calendar settings toggle for the Monday report card.',
});
const MONDAY_REPORT_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Every Monday at 07:00 Konvo DMs you a card with last week’s report drafted: what you finished, what is in review and the time you logged. Send it with one button.',
	comment: 'Description under the Monday report toggle.',
});
const EOD_DIGEST_DESCRIPTOR = msg({
	message: 'End-of-day digest',
	comment: 'Calendar settings toggle for the 17:00 digest DM.',
});
const EOD_DIGEST_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'At 17:00 on weekdays Konvo DMs you what the day brought: mail, meetings, tasks done and still open, and time tracked.',
	comment: 'Description under the end-of-day digest toggle.',
});
const WEEKLY_TIME_DESCRIPTOR = msg({
	message: 'Weekly time summary',
	comment: 'Calendar settings toggle for the Friday time summary DM.',
});
const WEEKLY_TIME_DESCRIPTION_DESCRIPTOR = msg({
	message: 'On Friday afternoons Konvo DMs you the hours you tracked in ClickUp this week, per day and per task.',
	comment: 'Description under the weekly time summary toggle.',
});

export const KonvoCalendarSettingsTab = observer(() => {
	const {i18n} = useLingui();
	useEffect(() => {
		void KonvoCalendarPrefs.load().then(() => KonvoCalendarPrefs.refreshCalendars());
	}, []);
	const weekStartOptions = useMemo(
		(): ReadonlyArray<SegmentedOption<'monday' | 'sunday'>> => [
			{value: 'monday', children: i18n._(WEEK_START_MONDAY_DESCRIPTOR)},
			{value: 'sunday', children: i18n._(WEEK_START_SUNDAY_DESCRIPTOR)},
		],
		[i18n],
	);
	const calendars = KonvoCalendarPrefs.calendars;
	const hexToInt = (hex: string | null): number => {
		if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return 0x5865f2;
		return Number.parseInt(hex.slice(1), 16);
	};
	const intToHex = (value: number): string => `#${(value & 0xffffff).toString(16).padStart(6, '0')}`;
	const setCalendarColor = (id: string, value: number | null) => {
		const colors = {...KonvoCalendarPrefs.calendarColors};
		if (value === null) {
			delete colors[id];
		} else {
			colors[id] = intToHex(value);
		}
		void KonvoCalendarPrefs.save({calendarColors: colors}).then(() => void KonvoCalendar.refresh());
	};
	const toggleCalendar = (id: string, next: boolean) => {
		void KonvoCalendarPrefs.setCalendarShown(id, next).then(() => void KonvoCalendar.refresh());
	};
	return (
		<SettingsTabContainer data-flx="user.konvo-calendar-tab.container">
			<SettingsTabContent data-flx="user.konvo-calendar-tab.content">
				<SettingsSection
					id="konvo-calendar-week-start"
					title={<Trans>Start of the week</Trans>}
					description={<Trans>Which day your week and month views begin on.</Trans>}
					data-flx="user.konvo-calendar-tab.section.week-start"
				>
					<Segmented<'monday' | 'sunday'>
						value={KonvoCalendarPrefs.weekStart}
						options={weekStartOptions}
						onChange={(value) => {
							void KonvoCalendarPrefs.save({weekStart: value}).then(() => void KonvoCalendar.refresh());
						}}
						aria-label={i18n._(START_OF_THE_WEEK_DESCRIPTOR)}
						className={styles.weekStart}
						data-flx="user.konvo-calendar-tab.week-start"
					/>
				</SettingsSection>
				<SettingsSection
					id="konvo-calendar-calendars"
					title={<Trans>Calendars</Trans>}
					description={<Trans>Choose which Outlook calendars show up in Konvo.</Trans>}
					data-flx="user.konvo-calendar-tab.section.calendars"
				>
					{calendars.length === 0 ? (
						<p style={{color: 'var(--text-secondary, #b5bac1)', fontSize: 13, margin: 0}}>
							<Trans>Connect your Outlook calendar on the Calendar page to pick calendars here.</Trans>
						</p>
					) : (
						<div className={styles.rows} data-flx="user.konvo-calendar-tab.calendar-rows">
							{calendars.map((calendar) => {
								const hasOverride = calendar.id in KonvoCalendarPrefs.calendarColors;
								return (
									<Switch
										key={calendar.id}
										label={calendar.name}
										description={calendar.owner ?? undefined}
										value={KonvoCalendarPrefs.isCalendarShown(calendar.id)}
										onChange={(next) => toggleCalendar(calendar.id, next)}
										accessory={
											<ColorPickerField
												variant="swatch"
												swatchSize={20}
												swatchLabel={calendar.name}
												value={hexToInt(KonvoCalendarPrefs.colorFor(calendar.id))}
												onChange={(value) => setCalendarColor(calendar.id, value)}
												isDefaultValue={!hasOverride}
												onReset={() => setCalendarColor(calendar.id, null)}
												className={styles.swatch}
												data-flx="user.konvo-calendar-tab.calendar-color"
											/>
										}
										data-flx="user.konvo-calendar-tab.calendar-toggle"
									/>
								);
							})}
						</div>
					)}
				</SettingsSection>
				<SettingsSection
					id="konvo-calendar-auto-presence"
					title={<Trans>Automatic status</Trans>}
					data-flx="user.konvo-calendar-tab.section.auto-presence"
				>
					<div className={styles.rows} data-flx="user.konvo-calendar-tab.automatic-rows">
						<Switch
							label={i18n._(AUTO_PRESENCE_DESCRIPTOR)}
							description={i18n._(AUTO_PRESENCE_DESCRIPTION_DESCRIPTOR)}
							value={KonvoCalendarPrefs.autoPresence}
							onChange={(next) => void KonvoCalendarPrefs.save({autoPresence: next})}
							icon={<CalendarCheckIcon size={20} />}
							data-flx="user.konvo-calendar-tab.auto-presence"
						/>
						<Switch
							label={i18n._(MEETING_REMINDERS_DESCRIPTOR)}
							description={i18n._(MEETING_REMINDERS_DESCRIPTION_DESCRIPTOR)}
							value={KonvoCalendarPrefs.meetingReminders}
							onChange={(next) => void KonvoCalendarPrefs.save({meetingReminders: next})}
							icon={<BellIcon size={20} />}
							data-flx="user.konvo-calendar-tab.meeting-reminders"
						/>
						<Switch
							label={i18n._(DAILY_AGENDA_DESCRIPTOR)}
							description={i18n._(DAILY_AGENDA_DESCRIPTION_DESCRIPTOR)}
							value={KonvoCalendarPrefs.dailyAgenda}
							onChange={(next) => void KonvoCalendarPrefs.save({dailyAgenda: next})}
							icon={<SunIcon size={20} />}
							data-flx="user.konvo-calendar-tab.daily-agenda"
						/>
						<Switch
							label={i18n._(MONDAY_REPORT_DESCRIPTOR)}
							description={i18n._(MONDAY_REPORT_DESCRIPTION_DESCRIPTOR)}
							value={KonvoCalendarPrefs.mondayReport}
							onChange={(next) => void KonvoCalendarPrefs.save({mondayReport: next})}
							icon={<ChartBarIcon size={20} />}
							data-flx="user.konvo-calendar-tab.monday-report"
						/>
						<Switch
							label={i18n._(EOD_DIGEST_DESCRIPTOR)}
							description={i18n._(EOD_DIGEST_DESCRIPTION_DESCRIPTOR)}
							value={KonvoCalendarPrefs.endOfDayDigest}
							onChange={(next) => void KonvoCalendarPrefs.save({endOfDayDigest: next})}
							icon={<SunHorizonIcon size={20} />}
							data-flx="user.konvo-calendar-tab.end-of-day-digest"
						/>
						<Switch
							label={i18n._(WEEKLY_TIME_DESCRIPTOR)}
							description={i18n._(WEEKLY_TIME_DESCRIPTION_DESCRIPTOR)}
							value={KonvoCalendarPrefs.weeklyTimeSummary}
							onChange={(next) => void KonvoCalendarPrefs.save({weeklyTimeSummary: next})}
							icon={<ClockIcon size={20} />}
							data-flx="user.konvo-calendar-tab.weekly-time-summary"
						/>
					</div>
				</SettingsSection>
				{Users.currentUser?.isStaff() === true && (
					<SettingsSection
						id="konvo-calendar-monday-people"
						title={<Trans>Who gets Monday Madness</Trans>}
						description={
							<Trans>
								Staff only. Everyone with Microsoft 365 connected is listed. Switch someone off and they stop getting
								the Monday card; they can also change it themselves above.
							</Trans>
						}
						data-flx="user.konvo-calendar-tab.section.monday-people"
					>
						<MondayReportPeople className={styles.rows} />
					</SettingsSection>
				)}
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default KonvoCalendarSettingsTab;
